import {
  Dispatch, SetStateAction, useEffect, useState,
} from 'react';
import cloneDeep from 'lodash.clonedeep';
import { useTheme } from 'styled-components';
import DataTable from '../../juristec-ui/core/DataTable';
import Avatar from '../../juristec-ui/core/Avatar';
import Button from '../../juristec-ui/core/Button';
import Tooltip from '../../juristec-ui/core/Tooltip';
import { agentOpts } from '../../options';
import {
  AgentsTable,
  AgentsTableOverFlow,
  UserInfoContainer,
  UserInfoText,
  AgentUser,
} from './styled';

interface UserAgents {
  [key: string]: {
    allowed: boolean,
    used?: number,
  };
}

interface User {
  uid: string;
  name: string;
  photoUrl: string;
  email: string;
  roleLabel: string;
}

interface DataState {
  validUsers: User[];
  agentsByUsers: Record<string, UserAgents>;
  agentsUsed: Record<string, number>;
}

interface VSManagerProps {
  allowedAgents: typeof agentOpts[number]['value'],
  userAgentsChangesState: [
    Record<string, UserAgents>,
    Dispatch<SetStateAction<Record<string, UserAgents>>>,
  ];
  getAgentsUsers: () => Promise<DataState>;
}

const userInfo = (user: User) => (
  <UserInfoContainer>
    <Avatar name={user.name} src={user.photoUrl || undefined} size="medium" />
    <UserInfoText>
      <h4>{user.name}</h4>
      <span>{user.email}</span>
      <span>{user.roleLabel}</span>
    </UserInfoText>
  </UserInfoContainer>
);

const agentToggle = (
  agentUsed: number,
  state: boolean,
  setter: () => void,
) => (
  <AgentUser>
    <span>{agentUsed}</span>
    {state ? (
      <Tooltip text="Clique para desabilitar">
        <Button size="small" color="success" onClick={setter}>
          Habilitado
        </Button>
      </Tooltip>
    ) : (
      <Tooltip text="Clique para habilitar">
        <Button size="small" color="error" onClick={setter}>
          Desabilitado
        </Button>
      </Tooltip>
    )}
  </AgentUser>
);

const VSManager = ({
  allowedAgents, userAgentsChangesState, getAgentsUsers,
}: VSManagerProps) => {
  const theme = useTheme() as Record<string, string>;
  const [data, setData] = useState<DataState>({
    validUsers: [],
    agentsByUsers: {},
    agentsUsed: {},
  });
  const [changes, setChanges] = userAgentsChangesState;

  useEffect(() => {
    (async () => {
      const res = await getAgentsUsers();
      setData(res);
      setChanges(cloneDeep(res.agentsByUsers));
    })();
  }, []);

  const handleChange = (
    agent: string,
    uid: string,
  ) => {
    setChanges((o) => {
      const old = { ...o };
      old[uid][agent].allowed = !o[uid][agent].allowed;
      return old;
    });
  };

  const columns = [
    {
      field: 'name',
      role: 'primaryInfo',
      label: 'Usuário',
      sortable: true,
      valueGetter: (param: User) => userInfo(param),
    },
    allowedAgents.includes('chat') && {
      field: 'chat',
      label: `Assistente de Base (${data.agentsUsed?.chat})`,
      valueGetter: (param: User) => agentToggle(
        data.agentsByUsers[param.uid]?.chat?.used ?? 0,
        changes[param.uid]?.chat?.allowed ?? true,
        () => handleChange('chat', param.uid),
      ),
    },
    allowedAgents.includes('insight') && {
      field: 'insight',
      label: `Smart Insight (${data.agentsUsed?.insight})`,
      valueGetter: (param: User) => agentToggle(
        data.agentsByUsers[param.uid]?.insight?.used ?? 0,
        changes?.[param.uid]?.insight?.allowed ?? true,
        () => handleChange('insight', param.uid),
      ),
    },
    allowedAgents.includes('quickDash') && {
      field: 'quickDash',
      label: `KPI's Rápidos (${data.agentsUsed?.quickDash})`,
      valueGetter: (param: User) => agentToggle(
        data.agentsByUsers[param.uid]?.quickDash?.used ?? 0,
        changes?.[param.uid]?.quickDash?.allowed ?? true,
        () => handleChange('quickDash', param.uid),
      ),
    },
    allowedAgents.includes('dashSummary') && {
      field: 'dashSummary',
      label: `Resumos (${data.agentsUsed?.dashSummary})`,
      valueGetter: (param: User) => agentToggle(
        data.agentsByUsers[param.uid]?.dashSummary?.used ?? 0,
        changes[param.uid]?.dashSummary?.allowed ?? true,
        () => handleChange('dashSummary', param.uid),
      ),
    },
  ];

  return (
    <AgentsTable>
      <AgentsTableOverFlow>
        <DataTable
          columns={columns}
          rowData={data.validUsers}
          // @ts-expect-error not typed yet
          strippedRowsColor={theme.containerOdd}
          defaultSortField="roleLabel"
          defaultSortOrder="ascending"
          // @ts-expect-error not typed yet
          headerColor="transparent"
          rowColor={theme.tableBackground}
          // @ts-expect-error not typed yet
          theadStyle={{
            position: 'sticky',
            top: 0,
            backgroundColor: theme.background,
          }}
        />
      </AgentsTableOverFlow>
    </AgentsTable>
  );
};

export default VSManager;
